import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Truck, 
  CheckCircle, 
  AlertTriangle,
  Plus,
  FileText,
  BarChart3,
  Clock,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  getDashboardStats, 
  getUtilizationStats, 
  getHireActivity, 
  getRecentAlerts 
} from '../lib/api';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

const COLORS = ['#FF5F00', '#1A202C', '#F6E05E', '#10B981', '#3B82F6', '#8B5CF6'];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [utilization, setUtilization] = useState([]);
  const [activity, setActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, utilRes, activityRes, alertsRes] = await Promise.all([
          getDashboardStats(),
          getUtilizationStats(),
          getHireActivity(),
          getRecentAlerts()
        ]);
        setStats(statsRes.data);
        setUtilization(utilRes.data);
        setActivity(activityRes.data);
        setAlerts(alertsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const kpiCards = [
    { label: 'Total Assets', value: stats?.total_assets || 0, icon: Package, color: 'text-slate-700' },
    { label: 'On Hire', value: stats?.on_hire || 0, icon: Truck, color: 'text-blue-600' },
    { label: 'Available', value: stats?.available || 0, icon: CheckCircle, color: 'text-green-600' },
    { label: 'Requiring Attention', value: stats?.requiring_attention || 0, icon: AlertTriangle, color: 'text-amber-600' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl text-slate-900 uppercase tracking-tight">
            Dashboard
          </h1>
          <p className="font-body text-slate-500 mt-1">
            Overview of your hire operations
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            asChild
            className="bg-primary hover:bg-primary-hover text-white font-heading font-bold uppercase tracking-wide shadow-industrial-sm"
            data-testid="new-hire-btn"
          >
            <Link to="/hire-orders">
              <Plus className="w-4 h-4 mr-2" />
              New Hire Order
            </Link>
          </Button>
          <Button 
            asChild
            variant="outline"
            className="border-2 border-slate-300 font-heading font-bold uppercase tracking-wide shadow-industrial"
            data-testid="add-asset-btn"
          >
            <Link to="/assets">
              <Package className="w-4 h-4 mr-2" />
              Add Asset
            </Link>
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, index) => (
          <Card 
            key={index} 
            className="border-2 border-slate-200 shadow-industrial"
            data-testid={`kpi-${kpi.label.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="kpi-value">{kpi.value}</p>
                  <p className="kpi-label">{kpi.label}</p>
                </div>
                <kpi.icon className={`w-8 h-8 ${kpi.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Asset Utilization Chart */}
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading font-bold uppercase tracking-wide text-sm flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Asset Utilization by Category
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {utilization.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={utilization}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="category"
                    label={({ category, count }) => `${category}: ${count}`}
                  >
                    {utilization.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hire Activity Timeline */}
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading font-bold uppercase tracking-wide text-sm flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Hire Activity (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {activity.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={activity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="hires" 
                    stroke="#FF5F00" 
                    strokeWidth={3}
                    dot={{ fill: '#FF5F00', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                No activity data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alerts & Notifications */}
        <Card className="lg:col-span-2 border-2 border-slate-200 shadow-industrial">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading font-bold uppercase tracking-wide text-sm flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Alerts & Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {alerts.length === 0 ? (
                <div className="p-6 text-center text-slate-400">
                  No alerts at this time
                </div>
              ) : (
                alerts.slice(0, 5).map((alert) => (
                  <div 
                    key={alert.id} 
                    className={`p-4 flex items-start gap-3 ${
                      alert.type === 'damage_reported' ? 'bg-red-50' :
                      alert.type === 'overdue' ? 'bg-amber-50' :
                      'bg-slate-50'
                    }`}
                  >
                    <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      alert.type === 'damage_reported' ? 'text-red-500' :
                      alert.type === 'overdue' ? 'text-amber-500' :
                      'text-slate-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-slate-700">{alert.message}</p>
                      <p className="font-mono text-xs text-slate-400 mt-1">
                        {new Date(alert.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading font-bold uppercase tracking-wide text-sm">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2">
              <Button 
                asChild
                variant="outline"
                className="w-full justify-between border-2 border-slate-200 hover:border-primary"
              >
                <Link to="/hire-orders">
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Create New Hire Order
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="w-full justify-between border-2 border-slate-200 hover:border-primary"
              >
                <Link to="/assets">
                  <span className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add New Asset
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="w-full justify-between border-2 border-slate-200 hover:border-primary"
              >
                <Link to="/reports">
                  <span className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Run a Report
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
